import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Building2, CheckCircle2, ArrowRight, Copy, Check, KeyRound, CalendarClock, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { getRef } from '../../lib/attribution';
import { CalendlyInlineWidget } from './CalendlyInlineWidget';
import { grantDemoAccess } from '../../contexts/DemoContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || '';

const FACILITY_TYPES = ['Hospital', 'Health system', 'Payer / Insurer', 'SNF / Post-acute', 'ACO', 'Other'];
const VOLUMES = ['< 50 / mo', '50–200 / mo', '200–500 / mo', '500+ / mo'];

export const InstitutionInquiryModal = ({ open, onOpenChange, defaultInterest = 'demo', source = 'institutions_banner', onIssued }) => {
  const [form, setForm] = useState({
    org_name: '', contact_name: '', email: '', role: '',
    facility_type: '', monthly_discharges: '', phone: '', message: '',
  });
  const [interest, setInterest] = useState(defaultInterest);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [issuedKey, setIssuedKey] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  // Booking-vs-form modes: when Calendly is configured, default to
  // "Pick a time" so visitors get the calendar embed first. The
  // "Send a note" form is always reachable via the tab switch.
  const [mode, setMode] = useState(CALENDLY_URL ? 'book' : 'form');

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Sync the interest toggle + clear the success screen whenever the modal opens.
  useEffect(() => {
    if (open) {
      setInterest(defaultInterest);
      setDone(false);
      setIssuedKey(null);
      setMode(CALENDLY_URL ? 'book' : 'form');
    }
  }, [open, defaultInterest]);

  const reset = () => {
    setForm({ org_name: '', contact_name: '', email: '', role: '', facility_type: '', monthly_discharges: '', phone: '', message: '' });
    setInterest(defaultInterest); setDone(false); setIssuedKey(null);
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(issuedKey).then(() => { setKeyCopied(true); setTimeout(() => setKeyCopied(false), 1400); }).catch(() => {});
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error('Organization, name and work email are required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/institution-inquiry`, { ...form, interest, source, ref: getRef() });
      if (data.api_key) {
        setIssuedKey(data.api_key);
        onIssued?.(data.api_key);
      }
      // Iter 179 · Grant demo-preview access for demo & pilot interests so
      // the prospect can enter /demo/preview immediately after booking.
      if (interest === 'demo' || interest === 'pilot') {
        grantDemoAccess({ org: form.org_name.trim() });
      }
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => { if (!v) setTimeout(reset, 200); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="institution-inquiry-modal">
        {done ? (
          <div className="py-6 text-center" data-testid="institution-inquiry-success">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="cn-display text-2xl text-cn-text">{issuedKey ? 'Your sandbox key is ready' : 'Request received'}</h3>
            {issuedKey ? (
              <>
                <p className="text-sm text-cn-text-muted mt-2 max-w-sm mx-auto">
                  Copy it now — it&apos;s shown <span className="font-semibold">only once</span>. We&apos;ve also emailed it to you.
                </p>
                <div className="mt-4 flex items-center gap-2 bg-cn-ink-900 rounded-xl p-3 text-left">
                  <KeyRound className="w-4 h-4 text-cn-accent shrink-0" />
                  <code className="text-slate-200 text-xs font-mono flex-1 truncate" data-testid="institution-issued-key">{issuedKey}</code>
                  <button onClick={copyKey} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white" data-testid="institution-copy-key">
                    {keyCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                  <Link to={`/developers?key=${encodeURIComponent(issuedKey)}`} onClick={() => handleOpenChange(false)} className="cn-btn cn-btn-primary justify-center" data-testid="institution-open-docs">
                    Open API docs <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button onClick={() => handleOpenChange(false)} className="cn-btn cn-btn-ghost justify-center" data-testid="institution-inquiry-close">Done</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-cn-text-muted mt-2 max-w-sm mx-auto">
                  Thanks — our team will reach out shortly to confirm next steps. A confirmation is on its way to your inbox.
                </p>
                {(interest === 'demo' || interest === 'pilot') && (
                  <div className="mt-5 rounded-2xl border border-cn-primary/30 bg-cn-primary/5 dark:bg-cn-accent/10 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-cn-primary dark:text-cn-accent font-bold mb-1">
                      Before we meet
                    </div>
                    <div className="cn-display text-lg text-cn-text">
                      Take a quick look inside CaraLoom.
                    </div>
                    <Link
                      to="/demo/preview"
                      onClick={() => handleOpenChange(false)}
                      className="mt-3 inline-flex cn-btn cn-btn-primary text-sm font-bold"
                      data-testid="institution-enter-preview"
                    >
                      Enter CaraLoom Preview <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                )}
                <button onClick={() => handleOpenChange(false)} className="cn-btn cn-btn-ghost mt-4" data-testid="institution-inquiry-close">
                  Done
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="w-11 h-11 rounded-xl bg-cn-primary/10 flex items-center justify-center mb-2">
                <Building2 className="w-5 h-5 text-cn-primary" />
              </div>
              <DialogTitle className="cn-display text-2xl">Talk to our enterprise team</DialogTitle>
              <DialogDescription>
                One-click discharge, live license &amp; insurance compliance, and readmission-risk tracking — for hospitals, health systems, and payers.
              </DialogDescription>
            </DialogHeader>

            {/* Booking-vs-form mode toggle — only visible when Calendly is wired. */}
            {CALENDLY_URL && (
              <div className="grid grid-cols-2 gap-1.5 p-1 bg-cn-soft/60 rounded-xl border border-cn-border mb-3 mt-2"
                    data-testid="institution-mode-toggle">
                <button type="button" onClick={() => setMode('book')}
                        data-testid="institution-mode-book"
                        className={`py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                          mode === 'book' ? 'bg-white dark:bg-cn-ink-800 shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'
                        }`}>
                  <CalendarClock className="w-4 h-4" /> Pick a time
                </button>
                <button type="button" onClick={() => setMode('form')}
                        data-testid="institution-mode-form"
                        className={`py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                          mode === 'form' ? 'bg-white dark:bg-cn-ink-800 shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'
                        }`}>
                  <Mail className="w-4 h-4" /> Send a note
                </button>
              </div>
            )}

            {mode === 'book' && CALENDLY_URL ? (
              <div data-testid="institution-book-pane">
                <CalendlyInlineWidget
                  prefill={{ name: form.contact_name, email: form.email }}
                  utm={{ source: source || 'institution_modal', medium: 'inline', campaign: interest }}
                  height={640}
                />
                <p className="text-[11px] text-cn-text-muted text-center mt-3">
                  Prefer to talk over email? <button type="button" onClick={() => setMode('form')} className="text-cn-primary underline" data-testid="institution-switch-to-form">Send a note instead</button>.
                </p>
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-3 mt-1">
              {/* Interest toggle */}
              <div className="grid grid-cols-3 gap-1.5 p-1 bg-cn-soft/60 rounded-xl border border-cn-border">
                {[
                  { k: 'demo', label: 'Book a demo' },
                  { k: 'pilot', label: 'Hospital pilot' },
                  { k: 'api_access', label: 'API access' },
                ].map((o) => (
                  <button key={o.k} type="button" onClick={() => setInterest(o.k)} data-testid={`institution-interest-${o.k}`}
                    className={`py-2 rounded-lg text-sm font-semibold transition-all ${interest === o.k ? 'bg-white dark:bg-cn-ink-800 shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}>
                    {o.label}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Organization *</Label>
                  <Input value={form.org_name} onChange={upd('org_name')} placeholder="Mercy General Hospital" data-testid="institution-org" />
                </div>
                <div>
                  <Label className="text-xs">Your name *</Label>
                  <Input value={form.contact_name} onChange={upd('contact_name')} placeholder="Dana Cho" data-testid="institution-name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Work email *</Label>
                  <Input type="email" value={form.email} onChange={upd('email')} placeholder="dana@mercygen.org" data-testid="institution-email" />
                </div>
                <div>
                  <Label className="text-xs">Role</Label>
                  <Input value={form.role} onChange={upd('role')} placeholder="Director of Case Management" data-testid="institution-role" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Facility type</Label>
                  <Select value={form.facility_type} onValueChange={(v) => setForm((f) => ({ ...f, facility_type: v }))}>
                    <SelectTrigger className="cn-input w-full" data-testid="institution-facility-type">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {FACILITY_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`institution-facility-type-opt-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Monthly discharge volume</Label>
                  <Select value={form.monthly_discharges} onValueChange={(v) => setForm((f) => ({ ...f, monthly_discharges: v }))}>
                    <SelectTrigger className="cn-input w-full" data-testid="institution-volume">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {VOLUMES.map((v) => (
                        <SelectItem key={v} value={v} data-testid={`institution-volume-opt-${v.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Anything specific? (optional)</Label>
                <Textarea value={form.message} onChange={upd('message')} rows={3} placeholder="We discharge ~250 patients/month and want to cut 30-day readmissions." data-testid="institution-message" />
              </div>

              <button type="submit" disabled={submitting} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="institution-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{interest === 'api_access' ? 'Request API access' : interest === 'pilot' ? 'Start a hospital pilot' : 'Book a demo'} <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-[11px] text-cn-text-muted text-center">No spam. We typically respond within one business day.</p>
            </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InstitutionInquiryModal;
